import React from 'react';
import { Divider } from '@mui/material';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  AI_COLORS, AI_FONTS,
  ImgSlot, TipBox, AudioPlayer, CheckItem, VideoDrive,
  PageHero, ContentWrap, SectionTitle, SubTitle, Card,
} from '../shared';

// Imagens (importadas)
import img32 from '../../../imgs/adulto-idosos/32.png';
import img33 from '../../../imgs/adulto-idosos/33.png';
import img34 from '../../../imgs/adulto-idosos/34.png';
import img35 from '../../../imgs/adulto-idosos/35.png';
import img36 from '../../../imgs/adulto-idosos/36.png';
import img37 from '../../../imgs/adulto-idosos/37.png';
import img38 from '../../../imgs/adulto-idosos/38.png';
import img39 from '../../../imgs/adulto-idosos/39.png';
import img40 from '../../../imgs/adulto-idosos/40.png';
import img41 from '../../../imgs/adulto-idosos/41.png';
import img42 from '../../../imgs/adulto-idosos/42.png';
import img43 from '../../../imgs/adulto-idosos/43.png';
import img44 from '../../../imgs/adulto-idosos/44.png';
import img45 from '../../../imgs/adulto-idosos/45.png';
import img46 from '../../../imgs/adulto-idosos/46.png';
import img47 from '../../../imgs/adulto-idosos/47.png';
import img57 from '../../../imgs/adulto-idosos/57.png';
import img58 from '../../../imgs/adulto-idosos/58.png';

export default function AgravosBucaisAdulto() {
  const isMobile = useIsMobile();

  return (
    <div style={{ fontFamily: AI_FONTS.body, background: 'transparent' }}>
      <PageHero
        title="Agravos Bucais"
        subtitle="Conhecer os problemas que podem aparecer na boca é o primeiro passo para preveni-los e tratá-los adequadamente. Cada tópico abaixo tem um áudio com o resumo."
      />

      <ContentWrap>
        {/* ── CÁRIE ── */}
        <SectionTitle style={{ marginTop: 0 }}>Cárie Dentária</SectionTitle>
        <AudioPlayer src="carie.m4a" />
        <p style={{ lineHeight: 1.75 }}>
          A cárie acontece quando bactérias presentes na boca utilizam restos de alimentos —
          principalmente o açúcar — e produzem substâncias que podem desgastar e danificar os
          dentes. No começo, ela pode passar despercebida, mas, se não for cuidada, pode aumentar e
          causar problemas maiores.
        </p>

        <VideoDrive id="1Pls5WpYNNZSMbZUUoJdZCKUXstGSQQtH" title="Cárie dentária" />

        <SubTitle>O que pode aumentar o risco de cárie?</SubTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 12,
            marginBottom: 16,
          }}
        >
          {[
            'Presença de bactérias na boca',
            'Consumir alimentos e bebidas com muito açúcar com frequência',
            'Escovar os dentes de forma inadequada',
            'Passar muito tempo sem escovar os dentes',
            'Não usar fio dental diariamente',
            'Acúmulo de restos de alimentos nos dentes',
            'Boca seca ou pouca produção de saliva',
            'Não realizar consultas regulares ao dentista',
          ].map((item, i) => (
            <Card key={i} variant={i}>
              <span style={{ color: '#EF5350', marginRight: 8 }}>✔</span>
              {item}
            </Card>
          ))}
        </div>

        <p style={{ fontWeight: 600, color: AI_COLORS.blue900 }}>Pode causar:</p>
        <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
          <li>Dor</li>
          <li>Sensibilidade</li>
          <li>Fratura do dente</li>
        </ul>

        <SubTitle>Evolução da cárie</SubTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              background: AI_COLORS.bgBlue50,
              borderRadius: 12,
              padding: '16px 20px',
            }}
          >
            <strong
              style={{
                fontFamily: AI_FONTS.title,
                color: AI_COLORS.blue900,
                display: 'block',
                marginBottom: 8,
              }}
            >
              O início da cárie
            </strong>
            <ul style={{ paddingLeft: 16, margin: 0, lineHeight: 1.9 }}>
              <li>Manchas brancas ou opacas no dente</li>
              <li>Mudanças na cor do dente</li>
              <li>Sensação de que o dente está mais áspero</li>
              <li>Pode haver sensibilidade a alimentos gelados, quentes ou doces</li>
            </ul>
            <ImgSlot
              filename={img32}
              alt="Início da cárie — manchas brancas ou opacas"
              maxWidth={240}
            />
          </div>
          <div
            style={{
              background: AI_COLORS.bgPurple50,
              borderRadius: 12,
              padding: '16px 20px',
            }}
          >
            <strong
              style={{
                fontFamily: AI_FONTS.title,
                color: AI_COLORS.blue900,
                display: 'block',
                marginBottom: 8,
              }}
            >
              A cárie depois de um tempo
            </strong>
            <ul style={{ paddingLeft: 16, margin: 0, lineHeight: 1.9 }}>
              <li>Manchas escuras</li>
              <li>Buracos nos dentes</li>
              <li>Quebra de parte do dente</li>
              <li>Acúmulo de alimentos no local</li>
              <li>Dor ao mastigar</li>
              <li>Sensibilidade a alimentos gelados, quentes ou doces</li>
              <li>Dor que pode surgir espontaneamente</li>
              <li>Mau hálito em alguns casos</li>
            </ul>
            <ImgSlot
              filename={img33}
              alt="Cárie avançada — buracos e quebra do dente"
              maxWidth={240}
            />
          </div>
        </div>

        <p style={{ lineHeight: 1.75 }}>
          No vídeo abaixo você poderá observar como é a evolução da cárie:
        </p>
        <VideoDrive id="1Rp0VRrXtTsFrJr3-DxVnlOCNZbQqkzIl" title="Evolução da cárie" />

        <p style={{ lineHeight: 1.75 }}>
          Quando a cárie não é tratada, ela pode atingir a parte mais interna do dente, onde ficam
          os nervos — causando dor forte e contínua. Quando isso acontece, pode ser necessário
          realizar um <strong>tratamento de canal</strong>. O vídeo abaixo mostra as etapas desse
          tratamento.
        </p>
        <VideoDrive id="16DQQV68wkNSmcjNeJAhXfzaXZPXe3ftE" title="Tratamento da cárie" />

        <p style={{ fontWeight: 600, color: AI_COLORS.blue900, marginBottom: 8 }}>Dicas:</p>
        <ul style={{ padding: 0, margin: '0 0 8px' }}>
          <CheckItem>Escove os dentes pelo menos 3 vezes ao dia</CheckItem>
          <CheckItem>Use o fio dental todos os dias</CheckItem>
          <CheckItem>Evite excesso de açúcar</CheckItem>
          <CheckItem>Visite o cirurgião dentista regularmente, de preferência a cada 6 meses</CheckItem>
        </ul>

        <TipBox>
          Nem toda cárie causa dor no início. Por isso, consultas regulares ao dentista ajudam a
          identificar alterações antes que o problema fique maior.
        </TipBox>

        <Divider style={{ margin: '36px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* ── GENGIVITE ── */}
        <SectionTitle>Gengivite</SectionTitle>
        <AudioPlayer src="gengivite.m4a" />
        <p style={{ lineHeight: 1.75 }}>
          É a <strong>inflamação da gengiva</strong>. Acontece principalmente pelo acúmulo de
          bactérias e restos de alimentos nos dentes e próximo à gengiva.
        </p>

        <ImgSlot filename={img34} alt="Gengivite — inflamação da gengiva" maxWidth={420} />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 12,
            margin: '16px 0',
          }}
        >
          {[
            { icon: '🔴', sinal: 'Gengiva vermelha' },
            { icon: '🔴', sinal: 'Sangramento' },
            { icon: '🔴', sinal: 'Inchaço' },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                background: AI_COLORS.pink50,
                borderRadius: 10,
                padding: '14px 18px',
                textAlign: 'center',
                fontWeight: 600,
              }}
            >
              {item.icon} {item.sinal}
            </div>
          ))}
        </div>

        <SubTitle>Fatores que aumentam o risco:</SubTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 10,
            marginBottom: 16,
          }}
        >
          {[
            'Escovação incorreta',
            'Não usar o fio dental',
            'Acúmulo de restos de alimentos',
            'Diabetes',
            'Alterações hormonais, como durante a gestação',
            'Tabagismo',
            'Boca seca',
            'Alguns medicamentos',
            'Uso de próteses mal adaptadas',
            'Diminuição da frequência das consultas odontológicas',
          ].map((item, i) => (
            <Card key={i} variant={i} style={{ padding: '10px 14px' }}>
              <span style={{ color: AI_COLORS.blue400, marginRight: 8 }}>✔</span>
              {item}
            </Card>
          ))}
        </div>

        <TipBox icon="🌟">
          A boa notícia é que a gengivite pode ser totalmente reversível! Quando identificada no
          início e tratada corretamente, a gengiva pode voltar a ficar saudável.
        </TipBox>

        <Divider style={{ margin: '36px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* ── PERIODONTITE ── */}
        <SectionTitle>Periodontite</SectionTitle>
        <AudioPlayer src="periodontite.m4a" />
        <p style={{ lineHeight: 1.75 }}>
          É um problema mais avançado da gengiva, que pode acontecer quando a gengivite não é
          tratada. O acúmulo de bactérias ao redor dos dentes pode afetar não apenas a gengiva,
          mas também os tecidos e o osso que sustentam os dentes.
        </p>

        <ImgSlot filename={img35} alt="Periodontite — problema mais avançado da gengiva" maxWidth={420} />

        <p style={{ fontWeight: 600, color: AI_COLORS.blue900, marginBottom: 8 }}>
          Pode causar:
        </p>
        <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
          <li>Mau hálito</li>
          <li>Mobilidade dos dentes (os dentes ficam amolecidos)</li>
          <li>Perda dentária</li>
        </ul>

        Assista ao vídeo abaixo para entender, de forma simples e ilustrativa, como os restos de alimentos podem se acumular próximo à gengiva

        <VideoDrive id="1KUi84njVOxuX9AFFeJ1_fcbxLmPKLLG0" title="Periodontite" />

        <SubTitle>Alguns fatores podem aumentar o risco de desenvolver periodontite:</SubTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 10,
            marginBottom: 16,
          }}
        >
          {[
            'Não realizar uma boa escovação dos dentes',
            'Não usar fio dental',
            'Acúmulo de restos de alimentos, bactérias e tártaro',
            'Tabagismo',
            'Diabetes, principalmente quando não está controlada',
            'Alterações hormonais',
            'Estresse',
            'Boca seca',
            'Alguns medicamentos',
            'Diminuição das consultas odontológicas',
            'Histórico familiar de problemas na gengiva',
            'Sistema de defesa do organismo mais enfraquecido',
          ].map((item, i) => (
            <Card key={i} variant={i} style={{ padding: '10px 14px', fontSize: '0.93rem' }}>
              <span style={{ color: AI_COLORS.blue400, marginRight: 8 }}>✔</span>
              {item}
            </Card>
          ))}
        </div>

        A imagem abaixo mostra, de forma ilustrativa, o processo de inflamação na gengiva (gengivite) e a evolução dessa inflamação (periodontite)

        <ImgSlot filename={img36} alt="Fatores de risco da periodontite" maxWidth={420} referencia="Adaptado de Ministério da Saúde (2012)."/>

        <p style={{ lineHeight: 1.75 }}>
          A periodontite precisa de tratamento e acompanhamento profissional. A limpeza dentária
          feita de 6 em 6 meses ajuda a manter a boca saudável, removendo os restos de alimentos,
          bactérias e o tártaro que se acumulam nos dentes e na gengiva.
        </p>

        <TipBox icon="⚠️">
          Lembre-se: quando o tártaro já está presente, ele não pode ser removido apenas com a
          escovação em casa. A remoção precisa ser feita pelo dentista no consultório.
        </TipBox>

        <p style={{ lineHeight: 1.75 }}>
          O vídeo abaixo mostra, de forma simples e ilustrativa, como funciona a limpeza dentária no
          consultório odontológico:
        </p>
        <VideoDrive id="1Ho5CEqBTN2OXZBgsKEOgZNLNHBt1dUuV" title="Limpeza dentária no consultório" />

        <Divider style={{ margin: '36px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* ── CÂNCER BUCAL ── */}
        <SectionTitle>Câncer Bucal</SectionTitle>
        <AudioPlayer src="cancer-bucal.m4a" />
        <p style={{ lineHeight: 1.75 }}>
          O câncer de boca é uma doença em que células da boca começam a crescer de forma
          descontrolada, formando uma lesão ou "ferida" que não cicatriza. Ele pode aparecer em
          diferentes partes da boca e também pode atingir regiões próximas, como garganta e lábios.
        </p>
        <p style={{ lineHeight: 1.75 }}>
          Quando descoberto no início, o tratamento tem maiores chances de sucesso. Por isso,
          conhecer os sinais e procurar um cirurgião dentista regularmente é muito importante.
        </p>

        <SubTitle>Onde o câncer de boca pode aparecer?</SubTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 10,
            marginBottom: 16,
          }}
        >
          {[
            'Língua (principalmente as laterais)',
            'Assoalho da boca (embaixo da língua)',
            'Lábios',
            'Céu da boca',
            'Gengiva',
            'Bochechas',
            'Garganta / Orofaringe',
          ].map((local, i) => (
            <Card key={i} variant={i} style={{ padding: '10px 14px', textAlign: 'center' }}>
              {local}
            </Card>
          ))}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 14,
            marginBottom: 20,
          }}
        >
          <ImgSlot filename={img37} alt="Câncer de lábio" caption="Lábio" maxWidth={240} referencia="Neville et al. (2016)."/>
          <ImgSlot filename={img38} alt="Câncer na borda lateral da língua" caption="Borda lateral de língua" maxWidth={240} referencia="Neville et al. (2016)."/>
          <ImgSlot filename={img39} alt="Câncer na gengiva" caption="Gengiva" maxWidth={240} referencia="Neville et al. (2016)."/>
          <ImgSlot filename={img40} alt="Câncer na borda lateral da língua" caption="Borda lateral de língua" maxWidth={240} referencia="Neville et al. (2016)."/>
        </div>

        <SubTitle>Quem pode desenvolver?</SubTitle>
        <p style={{ lineHeight: 1.75 }}>
          O câncer de boca pode acontecer em qualquer pessoa, mas é mais comum em:
        </p>
        <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
          <li>Adultos acima dos 40 anos</li>
          <li>Idosos</li>
          <li>Homens (embora os casos em mulheres estejam aumentando)</li>
          <li>Pessoas fumantes e/ou que consomem álcool frequentemente</li>
        </ul>

        <SubTitle>Fatores de risco</SubTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 12,
            marginBottom: 16,
          }}
        >
          {[
            { icon: '🚬', fator: 'Tabagismo', desc: 'Cigarro, eletrônico/vape, charuto, cachimbo, narguilé. Quanto maior o tempo e quantidade de uso, maior o risco.' },
            { icon: '🍺', fator: 'Consumo excessivo de álcool', desc: 'O álcool irrita a mucosa da boca e potencializa o efeito do cigarro.' },
            { icon: '☀️', fator: 'Exposição ao sol', desc: 'Principalmente para câncer de lábio inferior, em pessoas que trabalham expostas ao sol sem proteção.' },
            { icon: '🦠', fator: 'Infecção por HPV', desc: 'Alguns tipos do HPV estão associados a cânceres na região da garganta e boca.' },
            { icon: '😬', fator: 'Má higiene bucal e irritações', desc: 'Próteses mal adaptadas, dentes quebrados e higiene inadequada podem contribuir para irritações contínuas.' },
            { icon: '👨‍👩‍👧', fator: 'Histórico familiar e baixa imunidade', desc: 'Também podem influenciar no desenvolvimento da doença.' },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                background: i % 2 === 0 ? AI_COLORS.bgBlue50 : AI_COLORS.bgPurple50,
                borderRadius: 10,
                padding: '14px 18px',
              }}
            >
              <strong
                style={{
                  fontFamily: AI_FONTS.title,
                  color: AI_COLORS.blue900,
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                {item.icon} {item.fator}
              </strong>
              <p style={{ margin: 0, fontSize: '0.93rem', lineHeight: 1.6 }}>{item.desc}</p>
            </div>
          ))}
        </div>

        <SubTitle>Quais são os sinais e sintomas?</SubTitle>
        <p style={{ lineHeight: 1.75 }}>
          O câncer de boca pode começar de forma silenciosa. Os principais sinais de alerta são:
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 10,
            marginBottom: 16,
          }}
        >
          {[
            'Ferida na boca que não cicatriza por mais de 15 dias',
            'Mancha branca, vermelha ou escura',
            'Caroço ou endurecimento',
            'Dor persistente',
            'Sangramento sem motivo aparente',
            'Mau hálito persistente',
            'Dificuldade para mastigar ou engolir',
            'Sensação de algo preso na garganta',
            'Dormência',
            'Dificuldade para falar',
            'Perda de peso sem explicação',
          ].map((sinal, i) => (
            <div
              key={i}
              style={{
                background: AI_COLORS.pink50,
                borderRadius: 8,
                padding: '10px 14px',
                borderLeft: `3px solid #EF9A9A`,
                fontSize: '0.93rem',
              }}
            >
              ⚠️ {sinal}
            </div>
          ))}
        </div>

        <TipBox icon="🔍">
          Qualquer alteração na boca que dure <strong>mais de 15 dias</strong> deve ser avaliada
          por um cirurgião dentista. O diagnóstico precoce pode salvar vidas.
        </TipBox>

        <SubTitle>Autoexame do câncer de boca</SubTitle>
        <p style={{ lineHeight: 1.75 }}>O autoexame ajuda a perceber alterações suspeitas. Observe:</p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <div>
            <p style={{ fontWeight: 600, color: AI_COLORS.blue900, margin: '0 0 8px' }}>Regiões a observar:</p>
            <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
              <li>Língua</li>
              <li>Gengivas</li>
              <li>Céu da boca</li>
              <li>Parte interna das bochechas</li>
              <li>Lábios</li>
              <li>Embaixo da língua</li>
            </ul>
            <p style={{ fontWeight: 600, color: AI_COLORS.blue900, margin: '12px 0 8px' }}>Procure por:</p>
            <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
              <li>Feridas</li>
              <li>Manchas</li>
              <li>Caroços</li>
              <li>Sangramentos</li>
              <li>Alterações de cor</li>
            </ul>
          </div>
          <ImgSlot
            filename={img41}
            alt="Autoexame do câncer de boca — regiões a observar"
            maxWidth={600}
            referencia="Adaptado de Topeka Oral Surgery (2026)."
          />
        </div>

        <TipBox>
          Nem toda ferida é câncer. Muitas lesões na boca são simples e benignas. Porém, alterações
          que não melhoram precisam ser avaliadas.
        </TipBox>

        <SubTitle>O câncer de boca tem cura?</SubTitle>
        <p style={{ lineHeight: 1.75 }}>
          <strong>Sim.</strong> Quando descoberto precocemente, as chances de cura são muito
          maiores. O problema é que muitas pessoas demoram para procurar ajuda, porque acreditam que
          a lesão "vai melhorar sozinha". Por isso, qualquer alteração na boca que dure mais de 15
          dias deve ser avaliada.
        </p>

        <SubTitle>Como prevenir?</SubTitle>
        <ul style={{ padding: 0, margin: '0 0 8px' }}>
          <CheckItem>Não fumar</CheckItem>
          <CheckItem>Evitar excesso de álcool</CheckItem>
          <CheckItem>Usar protetor labial com FPS, especialmente para quem trabalha no sol</CheckItem>
          <CheckItem>Manter boa higiene bucal</CheckItem>
          <CheckItem>Fazer consultas regulares ao cirurgião dentista</CheckItem>
          <CheckItem>Alimentação saudável — frutas, verduras e legumes ajudam na proteção do organismo</CheckItem>
          <CheckItem>Vacinação contra HPV</CheckItem>
        </ul>

        <Divider style={{ margin: '36px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* ── XEROSTOMIA ── */}
        <SectionTitle>Xerostomia (Boca Seca)</SectionTitle>
        <AudioPlayer src="xerostomia.m4a" />
        <p style={{ lineHeight: 1.75 }}>
          É a sensação de boca seca, causada pela diminuição da saliva.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 16,
            margin: '16px 0',
          }}
        >
          <div style={{ background: AI_COLORS.bgBlue50, borderRadius: 12, padding: '16px 20px' }}>
            <strong
              style={{ fontFamily: AI_FONTS.title, fontSize: '1.25rem', color: AI_COLORS.blue900, display: 'block', marginBottom: 8 }}
            >
              Pode estar relacionada a:
            </strong>
            <ul style={{ paddingLeft: 16, margin: 0, lineHeight: 1.9 }}>
              <li>Uso de alguns medicamentos</li>
              <li>Algumas doenças</li>
              <li>Envelhecimento</li>
              <li>Pouca ingestão de água</li>
              <li>Tabagismo</li>
            </ul>
          </div>
          <div style={{ background: AI_COLORS.bgPurple50, borderRadius: 12, padding: '16px 20px' }}>
            <strong
              style={{ fontFamily: AI_FONTS.title, fontSize: '1.25rem', color: AI_COLORS.blue900, display: 'block', marginBottom: 8 }}
            >
              Sinais comuns:
            </strong>
            <ul style={{ paddingLeft: 16, margin: 0, lineHeight: 1.9 }}>
              <li>Sensação de boca seca</li>
              <li>Dificuldade para mastigar ou engolir</li>
              <li>Mau hálito</li>
              <li>Ardência na boca</li>
              <li>Maior risco de cáries</li>
            </ul>
          </div>
        </div>

        <p style={{ fontWeight: 600, color: AI_COLORS.blue900, marginBottom: 8 }}>Dicas:</p>
        <ul style={{ padding: 0, margin: '0 0 12px' }}>
          <CheckItem>Beba água ao longo do dia</CheckItem>
          <CheckItem>Evite fumar</CheckItem>
          <CheckItem>Procure um cirurgião-dentista se a boca seca for frequente</CheckItem>
        </ul>

        <Divider style={{ margin: '36px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* ── AFTAS ── */}
        <SectionTitle>Aftas</SectionTitle>
        <AudioPlayer src="aftas.m4a" />
        <p style={{ lineHeight: 1.75 }}>
          A afta é uma das doenças mais comuns da boca. Apresenta-se como uma ferida avermelhada
          nas bordas e centro esbranquiçado, podendo aparecer na língua, na gengiva, na parte dos
          lábios e bochechas.
        </p>

        <ImgSlot
          filename={img42}
          alt="Afta — ferida avermelhada nas bordas e centro esbranquiçado"
          second={img43}
          referencia="Neville et al. (2021)."
        />

        <SubTitle>Fatores que podem aumentar as chances:</SubTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 10,
          }}
        >
          {[
            'Trauma — morder sem querer a língua ou a bochecha, ou ferimentos causados por próteses mal adaptadas',
            'Tabagismo',
            'Diabetes, principalmente quando não está controlada',
            'Alterações hormonais',
            'Estresse',
            'Boca seca',
            'Sistema de imunidade enfraquecido',
          ].map((item, i) => (
            <Card key={i} variant={i} style={{ padding: '10px 14px', fontSize: '0.93rem' }}>
              ✔ {item}
            </Card>
          ))}
        </div>

        <TipBox>
          O tratamento adequado para aftas depende da gravidade dos sintomas, da frequência, do
          tamanho e do número de feridas. Se você perceber sinais de afta ou qualquer outra ferida
          na boca, procure um cirurgião dentista para avaliação.
        </TipBox>

        <Divider style={{ margin: '36px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* ── HERPES ── */}
        <SectionTitle>Herpes Labial</SectionTitle>
        <AudioPlayer src="herpes.m4a" />
        <p style={{ lineHeight: 1.75 }}>
          O herpes simples tipo 1 é uma doença infecciosa que atinge regiões da boca,
          principalmente os lábios. É transmitido principalmente pelo compartilhamento de itens como
          batons, copos e talheres.
        </p>
        <p style={{ lineHeight: 1.75 }}>
          Começa com coceira na região, evolui para uma bolha que posteriormente estoura, causando
          uma ferida que costuma durar de 3 a 5 dias.
        </p>

        <ImgSlot
          filename={img44}
          alt="Herpes labial — fase de bolhas"
          second={img45}
          referencia="Neville et al. (2021)."
        />

        <TipBox>
          A herpes aparece principalmente em momentos em que a imunidade está baixa ou em episódios
          de estresse. Ainda não tem cura definitiva — o tratamento é feito com pomadas analgésicas
          com ação antiviral, mediante diagnóstico profissional.
        </TipBox>

        <Divider style={{ margin: '36px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* ── BRUXISMO ── */}
        <SectionTitle>Bruxismo e Apertamento Dentário</SectionTitle>
        <AudioPlayer src="bruxismo.m4a" />
        <p style={{ lineHeight: 1.75 }}>
          Acontecem quando a pessoa aperta ou range os dentes, geralmente de forma inconsciente,
          inclusive durante o sono.
        </p>

        Assista o vídeo abaixo que mostra, de forma ilustrativa, os dentes desgastados pelo bruxismo.

        <VideoDrive id="1aMQYhszVxaGWqe415tKa9CAFhXPb_tyf" title="Bruxismo e apertamento dentário" />

        <ImgSlot
          filename={img46}
          alt="Placa de mordida — usada à noite para prevenir o desgaste dental"
          second={img57}
          maxWidth={420}
          referencia="Canva (2026)."
          referencia2="Face Magazine (2019)."
        />


        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 12,
            margin: '16px 0',
          }}
        >
          {['Dor ao acordar', 'Dor de cabeça', 'Desgaste dental'].map((sinal, i) => (
            <div
              key={i}
              style={{
                background: AI_COLORS.bgPurple50,
                borderRadius: 10,
                padding: '14px 18px',
                textAlign: 'center',
                fontWeight: 600,
              }}
            >
              ⚠️ {sinal}
            </div>
          ))}
        </div>
        <TipBox>
          Procure orientação profissional. O uso de placas de mordida pode ser necessário para
          prevenir o desgaste dental — são usadas à noite. Cuidar da saúde emocional e reduzir o
          estresse também ajuda.
        </TipBox>

        <Divider style={{ margin: '36px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* ── LCNC ── */}
        <SectionTitle>Lesão Cervical Não Cariosa (LCNC)</SectionTitle>
        <AudioPlayer src="lcnc.m4a" />
        <p style={{ lineHeight: 1.75 }}>
          É o desgaste do dente que acontece próximo à gengiva, <strong>não</strong> causado por
          cárie.
        </p>

        <ImgSlot
          filename={img47}
          alt="Lesão Cervical Não Cariosa (LCNC) — desgaste próximo à gengiva"
          maxWidth={420}
          referencia="Face Magazine (2019)."
          second={img58}
        />

        <SubTitle>O que causa?</SubTitle>
        <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
          <li>Escovação com muita força</li>
          <li>Pacientes que usaram aparelho por muitos anos têm chance maior</li>
          <li>Consumo frequente de alimentos e bebidas ácidas</li>
          <li>Refluxo gástrico</li>
          <li>Bruxismo e sobrecarga oclusal</li>
        </ul>
        <p style={{ fontWeight: 600, color: AI_COLORS.blue900, margin: '12px 0 6px' }}>Pode causar:</p>
        <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
          <li>Sensibilidade</li>
          <li>Desgaste dental próximo da gengiva</li>
        </ul>
        <TipBox>Procure orientação profissional para tratamento adequado.</TipBox>

        <Divider style={{ margin: '36px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* ── BIOPSICOSSOCIAL ── */}
        <SectionTitle>O que é o Biopsicossocial?</SectionTitle>
        <AudioPlayer src="biopsicossocial.m4a" />
        <p style={{ lineHeight: 1.75 }}>
          Significa cuidar da sua saúde de forma completa, considerando três aspectos que estão
          ligados entre si:
        </p>

        <VideoDrive id="1bRpFWrjYAkt16cvlfgRoOaaiaLF11W_t" title="O que é o Biopsicossocial?" />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 16,
            margin: '16px 0 24px',
          }}
        >
          {[
            {
              label: 'Bio (biológico)',
              color: AI_COLORS.bgBlue50,
              desc: 'Cuidar do corpo e da saúde física: alimentação, sono, exercícios e tratamentos médicos',
            },
            {
              label: 'Psico (psicológico)',
              color: AI_COLORS.bgPurple50,
              desc: 'Cuidar das emoções, pensamentos, estresse e saúde mental',
            },
            {
              label: 'Social',
              color: AI_COLORS.teal50,
              desc: 'Cuidar dos relacionamentos, convivência com família, amigos, trabalho e hábitos do dia a dia',
            },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                background: item.color,
                borderRadius: 12,
                padding: '18px 20px',
                textAlign: 'center',
              }}
            >
              <strong
                style={{
                  fontFamily: AI_FONTS.title,
                  color: AI_COLORS.blue900,
                  display: 'block',
                  marginBottom: 8,
                  fontSize: '1.05rem',
                }}
              >
                {item.label}
              </strong>
              <p style={{ margin: 0, fontSize: '0.93rem', lineHeight: 1.6 }}>{item.desc}</p>
            </div>
          ))}
        </div>

        <TipBox style={{ textAlign: 'center' }} icon="💡">
          Cuidar da saúde de forma integral é importante porque ela não depende apenas do corpo,
          mas também das emoções e da forma como vivemos e nos relacionamos. Quando esses aspectos
          estão em equilíbrio, é possível reduzir o estresse, fortalecer o organismo e prevenir o
          surgimento ou agravamento de diversas doenças.
        </TipBox>
      </ContentWrap>
    </div>
  );
}
